/**
 * threadMessageListView.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import * as React from "react";

import { AuthContext } from "../auth/auth.context";

import { ChatInput } from "../chat/chatInput";
import { FileDownloader } from "./fileDownloader.component";
import { MessageDisplayComponent } from "./messageDisplay.component";

import { DisplayMessage, ContentMessage, MessageType, NotifyUser } from "../models/message";
import { Reaction } from "../models/reaction";

import { reportError } from "../services/error.service";
import { groupServiceFactory } from "../services/group.service";
import { AccountMemberMention } from "../editor/scEditor";

interface Props {
  threadID: string,
  canSendMessages: boolean | null,
  startTruncated?: boolean,
  mainMessage: DisplayMessage,
  reactingMessage?: ContentMessage | null,
  replies: Array<DisplayMessage>,
  getMatchingMembers(queryText: string, groupID: string): Promise<Array<AccountMemberMention>>,
  onFocus?: () => void,
  didExpandList?: () => void,
  showEmojiPicker?(anchor: HTMLElement, msg: ContentMessage): void,
  onError: (msg: string) => void
}

// can have multiple ThreadMessageListViews on the same page, one timeout per group
const threadEditingDraftTimeouts: {[id: string]: NodeJS.Timeout | null} = {};

export function ThreadMessageListView(props: Props): React.ReactElement<any, any> {
  const { threadID, canSendMessages, mainMessage } = props;
  const msgsHidden = props.startTruncated && props.replies.length > 2;
  const authContext = React.useContext<AuthContext>(AuthContext);
  //const groupNavContext = React.useContext<GroupNavContext>(GroupNavContext);
  const [fileData, setFileData] = React.useState<{fileID: string, keyID: string}>({fileID: "", keyID: ""});
  const [truncated, setTruncated] = React.useState<boolean>(msgsHidden || false);

  function getDraft(): Promise<string | null> {
    return groupServiceFactory().getDraft(threadID);
  }

  function saveDraft(draft: string): void {
    if (!authContext.membership || !mainMessage) { return; }
    const accountID = authContext.membership.accountID;
    const groupID = mainMessage.groupID;

    threadEditingDraftTimeouts[groupID] && clearTimeout(threadEditingDraftTimeouts[groupID]);
    threadEditingDraftTimeouts[groupID] = setTimeout(() => {
      threadEditingDraftTimeouts[groupID] = null;
      (draft ?
        groupServiceFactory().saveDraft(accountID, groupID, threadID, draft) :
        groupServiceFactory().deleteDraft(groupID, threadID))
      .then(null, err => props.onError(err.message));
    }, 2000);
  }

  async function doSendMessage(msg: ContentMessage, mentions: Array<NotifyUser> | undefined) {
    if (!authContext.membership || !mainMessage) { return; }
    try {
      const groupService = groupServiceFactory();
      const groupID = mainMessage.groupID;
      await groupService.sendMessage(authContext.membership.accountID,
        mainMessage.groupID,
        msg, mentions);

      threadEditingDraftTimeouts[groupID] && clearTimeout(threadEditingDraftTimeouts[groupID]);
      await groupService.deleteDraft(mainMessage.groupID, threadID);
      threadEditingDraftTimeouts[groupID] = null;
    } catch (err) {
      console.error("Error posting message:", err);
      reportError(err as Error, "sending message");
    }
  }
  function doFileDownload(fileID: string, keyID: string) {
    setFileData({fileID: fileID, keyID: keyID});
  }

  async function toggleReaction(msg: ContentMessage, reaction: Reaction) {
    if (!authContext.membership) { return; }
    if (!reaction.users) { reaction.users = []; }

    const users = reaction.users;
    const index = users.findIndex(u => u.userID === authContext.membership?.userID);
    const reactionThreadID = msg._id === threadID ? "" : msg.threadID;
    try {
      if (index >= 0) {
        const myReaction = users[index];
        await groupServiceFactory().sendRemoveReaction(
          authContext.membership.accountID, msg.groupID, msg.frameID, msg._id,
          reactionThreadID, myReaction.reactionID, reaction.contentID);

      } else {
        const {accountID, firstname, lastname} = authContext.membership;

        await groupServiceFactory().sendReaction(accountID,
          msg.groupID, msg._id, reactionThreadID,
          reaction.contentID, reaction.contenttype, reaction.content,
          firstname, lastname);
    
      }
    } catch (err) {
      console.error("Error toggling reaction:", err);
      props.onError((err as Error).message);
    }
  }

  async function doRemoveMessage(msgID: string, frameID: string | undefined, threadID: string | undefined) {
    if (!authContext.membership) { return; }
    try {
      const groupService = groupServiceFactory();
      if (threadID) {
        await groupService.removeReply(authContext.membership.accountID,
          mainMessage.groupID,
          threadID,
          msgID, frameID);
      } else {
        await groupService.removeMessage(authContext.membership.accountID,
          mainMessage.groupID,
          msgID, frameID);
      }
    } catch (err) {
      console.error("Error removing message:", err);
      props.onError((err as Error).message);
    }
  }

  async function doRemoveMessageLocally(msgID: string, _frameID: string | undefined, threadID: string | undefined) {
    if (!authContext.membership) { return; }
    try {
      if (threadID) {
        await groupServiceFactory().removeReplyLocally(authContext.membership.accountID,
          mainMessage.groupID, threadID, msgID);
      } else {
        await groupServiceFactory().removeMessageLocally(authContext.membership.accountID,
          mainMessage.groupID, msgID);
      }
    } catch (err) {
      console.error("Error removing message locally:", err);
      props.onError((err as Error).message);
    }
  }

  function expandMessageList() {
    setTruncated(false);
    props.didExpandList && props.didExpandList();
  }

  const replies = truncated ?
    props.replies.slice(props.replies.length - 2) :
    props.replies;

  return <>
    <div className="main-thread-message">
      <MessageDisplayComponent
        msg={mainMessage as ContentMessage}
        inThread={true}
        displayTZ={authContext.tz()}
        canWriteMessages={props.canSendMessages}
        maintainHover={mainMessage._id === props.reactingMessage?._id}
        myUserID={authContext.membership?.userID || ""}
        forceReactionContainer={true}
        showEmojiPicker={props.showEmojiPicker}
        doRemoveMessage={doRemoveMessage}
        doRemoveLocally={doRemoveMessageLocally}
        doDownloadFile={doFileDownload}
        doToggleReaction={toggleReaction} />
    </div>
      {truncated && <div className="thread-truncated" onClick={expandMessageList}>Show all messages</div>}
      {replies.map(msg => {
        switch (msg.msgtype) {
          case MessageType.USER_ADDED:
          case MessageType.DEV_ADDED:
          case MessageType.USER_JOINED:
          case MessageType.DEV_JOINED:
            // should never happen. You can't join a thread.
            return <></>;
          default:
            // older messages might be missing a msgtype
            return <MessageDisplayComponent key={msg._id}
              msg={msg as ContentMessage}
              inThread={true}
              displayTZ={authContext.tz()}
              canWriteMessages={props.canSendMessages}
              maintainHover={msg._id === props.reactingMessage?._id} 
              myUserID={authContext.membership?.userID || ""}
              showEmojiPicker={props.showEmojiPicker}
              doRemoveMessage={doRemoveMessage}
              doRemoveLocally={doRemoveMessageLocally}
              doDownloadFile={doFileDownload}
              doToggleReaction={toggleReaction} />;
        }
      })}

      { canSendMessages && <div className="thread-input-container">
        <ChatInput
          groupID={mainMessage?.groupID || ""}
          threadID={threadID}
          myUserID={authContext.membership?.userID}
          sendOnReturnKey={authContext.membership?.sendOnReturn || false}
          getDraft={getDraft}
          saveDraft={saveDraft}
          onSend={doSendMessage}
          getMatchingMembers={props.getMatchingMembers}
          onFocus={props.onFocus}
          onError={props.onError} />
      </div>}
      <FileDownloader groupID={mainMessage?.groupID || ""} {...fileData}
        didTrigger={() => setFileData({fileID: "", keyID: ""})}
        onError={() => setFileData({fileID: "", keyID: ""})} />
  </>;
}
